import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import authService from '../services/authService';
import { AuthLegalFooter } from './AuthLegalFooter';
import {
  AuthAlert,
  AuthField,
  AuthInput,
  AuthPageLayout,
  AuthSubmitButton,
  authStyles,
} from './AuthPageLayout';

export const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [username, setUsername] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!username.trim()) {
      setError(t('errors.emailRequired'));
      return;
    }

    setLoading(true);
    try {
      const result = await authService.forgotPassword(username.trim());
      navigate('/reset-password', {
        replace: true,
        state: {
          codeSent: true,
          email: username.trim(),
          message: result.message,
        },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unableToRequestReset'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageLayout title={t('forgotPasswordTitle')} subtitle={t('forgotPasswordSubtitle')} footer={<AuthLegalFooter />}>
      <form className={authStyles.form} onSubmit={onSubmit}>
        {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}
        {message ? <AuthAlert variant="success">{message}</AuthAlert> : null}

        <AuthField label={t('emailLabel')} htmlFor="forgot-email" icon="mail">
          <AuthInput
            id="forgot-email"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoComplete="email"
            placeholder={t('emailPlaceholder')}
          />
        </AuthField>

        <AuthSubmitButton loading={loading} loadingLabel={t('sending')}>
          {t('sendResetLink')}
        </AuthSubmitButton>

        <div className={authStyles.footerLinks}>
          <Link className={authStyles.link} to="/reset-password">
            {t('haveToken')}
          </Link>
          <Link className={authStyles.link} to="/login">
            {t('backToLogin')}
          </Link>
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default ForgotPasswordPage;
